import api from './api';
import type { ApiResponse, LeadStatus, LeadSource } from '@/types';

// =============================================================================
// Lead Statuses
// =============================================================================

export interface CreateLeadStatusData {
  name: string;
  color: string;
  is_default?: boolean;
  is_won?: boolean;
  is_lost?: boolean;
  is_qualified?: boolean;
  is_active?: boolean;
}

export interface UpdateLeadStatusData extends Partial<CreateLeadStatusData> {}

export const getLeadStatuses = async (options: { activeOnly?: boolean; withCounts?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (options.activeOnly) params.append('active_only', 'true');
  if (options.withCounts) params.append('with_counts', 'true');
  const response = await api.get<ApiResponse<LeadStatus[]>>(`/lead-statuses?${params}`);
  return response.data.data;
};

export const getLeadStatus = async (id: string) => {
  const response = await api.get<ApiResponse<LeadStatus>>(`/lead-statuses/${id}`);
  return response.data.data;
};

export const createLeadStatus = async (data: CreateLeadStatusData) => {
  const response = await api.post<ApiResponse<LeadStatus>>('/lead-statuses', data);
  return response.data.data;
};

export const updateLeadStatus = async (id: string, data: UpdateLeadStatusData) => {
  const response = await api.put<ApiResponse<LeadStatus>>(`/lead-statuses/${id}`, data);
  return response.data.data;
};

export const deleteLeadStatus = async (id: string) => {
  await api.delete(`/lead-statuses/${id}`);
};

export const reorderLeadStatuses = async (orderedIds: string[]) => {
  const response = await api.post<ApiResponse<LeadStatus[]>>('/lead-statuses/reorder', {
    order: orderedIds,
  });
  return response.data.data;
};

export interface LeadSystemDefinition {
  key: string;
  dimension: 'stage' | 'contact_result';
  label: string;
  legacy_outcome: string | null;
  supported: boolean;
  mappable?: boolean;
}
export interface LeadSystemCatalog {
  definitions: LeadSystemDefinition[];
  mappings: LeadStatus[];
}
export const getLeadSystemStatuses = async (signal?: AbortSignal) => {
  const response = await api.get<ApiResponse<LeadSystemCatalog>>('/lead-system-statuses', { signal });
  return response.data.data;
};

// =============================================================================
// Lead Sources
// =============================================================================

export interface CreateLeadSourceData {
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface UpdateLeadSourceData extends Partial<CreateLeadSourceData> {}

export const getLeadSources = async (options: { activeOnly?: boolean; withCounts?: boolean } = {}) => {
  const params = new URLSearchParams();
  if (options.activeOnly) params.append('active_only', 'true');
  if (options.withCounts) params.append('with_counts', 'true');
  const response = await api.get<ApiResponse<LeadSource[]>>(`/lead-sources?${params}`);
  return response.data.data;
};

export const getLeadSource = async (id: string) => {
  const response = await api.get<ApiResponse<LeadSource>>(`/lead-sources/${id}`);
  return response.data.data;
};

export const createLeadSource = async (data: CreateLeadSourceData) => {
  const response = await api.post<ApiResponse<LeadSource>>('/lead-sources', data);
  return response.data.data;
};

export const updateLeadSource = async (id: string, data: UpdateLeadSourceData) => {
  const response = await api.put<ApiResponse<LeadSource>>(`/lead-sources/${id}`, data);
  return response.data.data;
};

export const deleteLeadSource = async (id: string) => {
  await api.delete(`/lead-sources/${id}`);
};

// =============================================================================
// Roles & Permissions
// =============================================================================

export interface RoleWithDetails {
  id: number;
  name: string;
  display_name?: string;
  description?: string;
  type?: 'system' | 'default' | 'custom';
  is_active?: boolean;
  permissions: string[];
  users_count?: number;
  created_at?: string;
  updated_at?: string;
}

export interface Permission {
  id: number;
  name: string;
  group: string;
}

export interface PermissionsResponse {
  permissions: Permission[];
  grouped: Record<string, string[]>;
}

export interface CreateRoleData {
  name: string;
  display_name?: string;
  description?: string;
}

export interface UpdateRoleData {
  display_name?: string;
  description?: string;
}

export const getRoles = async () => {
  const response = await api.get<ApiResponse<RoleWithDetails[]>>('/roles');
  return response.data.data;
};

export const getRole = async (id: number) => {
  const response = await api.get<ApiResponse<RoleWithDetails>>(`/roles/${id}`);
  return response.data.data;
};

export const createRole = async (data: CreateRoleData) => {
  const response = await api.post<ApiResponse<RoleWithDetails>>('/roles', data);
  return response.data.data;
};

export const updateRole = async (id: number, data: UpdateRoleData) => {
  const response = await api.put<ApiResponse<RoleWithDetails>>(`/roles/${id}`, data);
  return response.data.data;
};

export const deleteRole = async (id: number) => {
  await api.delete(`/roles/${id}`);
};

export const cloneRole = async (id: number, data: { name: string; display_name?: string }) => {
  const response = await api.post<ApiResponse<RoleWithDetails>>(`/roles/${id}/clone`, data);
  return response.data.data;
};

export const updateRolePermissions = async (id: number, permissions: string[]) => {
  const response = await api.put<ApiResponse<RoleWithDetails>>(`/roles/${id}/permissions`, {
    permissions,
  });
  return response.data.data;
};

export const getRoleUsers = async (id: number) => {
  const response = await api.get<ApiResponse<Array<{ id: string; name: string; email: string }>>>(`/roles/${id}/users`);
  return response.data.data;
};

export const getPermissions = async (): Promise<PermissionsResponse> => {
  const response = await api.get<ApiResponse<PermissionsResponse>>('/permissions');
  return response.data.data;
};

// =============================================================================
// General Settings
// =============================================================================

export interface GeneralSettings {
  clinic_name: string;
  clinic_email: string;
  clinic_phone: string;
  clinic_address: string;
  default_timezone: string;
  default_currency: string;
  date_format: string;
  time_format: string;
  // Computed server-side from config/currency.php (not a settings row):
  // the currencies documents may be created in on this clinic.
  allowed_currencies?: string[];
  // Fiscalization — the legal basis sent with a zero-rated line, entered by
  // the clinic's accountant. The VAT rate is deliberately NOT here: it is the
  // tax the invoice itself charged. Nor is the exchange rate, which lives on
  // each invoice — one stored rate would report an old invoice at today's.
  fiscal_exemption_type?: string;
  // Language a NEW quote opens in ("" / null = guess from the lead).
  quote_default_language?: string | null;
}

// Public display settings — works for any authenticated user.
// Returns only general + regional groups (currency, timezone, etc.).
// Use this for non-admin consumers like the currency hook or inbox.
export const getGeneralSettings = async (): Promise<Record<string, unknown>> => {
  const response = await api.get<ApiResponse<Record<string, unknown>>>('/settings/public');
  return response.data.data;
};

// Full settings — admin-only endpoint, includes telephony and secrets.
// Used by the admin Settings UI.
export const getSettings = async (): Promise<Record<string, unknown>> => {
  const response = await api.get<ApiResponse<Record<string, unknown>>>('/settings');
  return response.data.data;
};

export const updateGeneralSettings = async (data: Partial<GeneralSettings>): Promise<Record<string, unknown>> => {
  const response = await api.put<ApiResponse<Record<string, unknown>>>('/settings', {
    settings: data,
  });
  return response.data.data;
};

export const getSettingsByGroup = async (group: string): Promise<Record<string, unknown>> => {
  const response = await api.get<ApiResponse<Record<string, unknown>>>(`/settings/group/${group}`);
  return response.data.data;
};

export const updateSettings = updateGeneralSettings;

// =============================================================================
// Fiscalization — read-only eBills wiring
// =============================================================================

/**
 * How this clinic is wired to eBills. Every field is set outside the app (env
 * / config/clinics.php), so this is diagnostic only — there is nothing here to
 * save. The token is never returned, only whether one is present.
 */
export interface FiscalizationStatus {
  /** Token + NIPT + base URL all present; reporting cannot work without it. */
  connected: boolean;
  token_configured: boolean;
  base_url: string;
  /** Invoices reported against `test` never reach the tax authority. */
  environment: 'test' | 'production';
  nipt: string | null;
  business_unit_code: string | null;
  default_operator_code: string | null;
  default_pos_id: number;
  /** The code the current user's invoices would carry. */
  operator_code: string | null;
  has_personal_operator_code: boolean;
}

export const getFiscalizationStatus = async (): Promise<FiscalizationStatus> => {
  const response = await api.get<ApiResponse<FiscalizationStatus>>(
    '/settings/fiscalization/status',
  );
  return response.data.data;
};

// =============================================================================
// Notification Templates
// =============================================================================

export interface NotificationTemplate {
  id: string;
  name: string;
  type: 'sms' | 'whatsapp' | 'email';
  subject: string | null;
  body: string;
  language: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateNotificationTemplateData {
  name: string;
  type: 'sms' | 'whatsapp' | 'email';
  subject?: string;
  body: string;
  language?: string;
  is_active?: boolean;
}

export interface UpdateNotificationTemplateData extends Partial<CreateNotificationTemplateData> {}

export const getNotificationTemplates = async (type?: 'sms' | 'whatsapp' | 'email') => {
  const params = new URLSearchParams();
  if (type) params.append('type', type);
  const response = await api.get<ApiResponse<NotificationTemplate[]>>(`/notification-templates?${params}`);
  return response.data.data;
};

export const getNotificationTemplate = async (id: string) => {
  const response = await api.get<ApiResponse<NotificationTemplate>>(`/notification-templates/${id}`);
  return response.data.data;
};

export const createNotificationTemplate = async (data: CreateNotificationTemplateData) => {
  const response = await api.post<ApiResponse<NotificationTemplate>>('/notification-templates', data);
  return response.data.data;
};

export const updateNotificationTemplate = async (id: string, data: UpdateNotificationTemplateData) => {
  const response = await api.put<ApiResponse<NotificationTemplate>>(`/notification-templates/${id}`, data);
  return response.data.data;
};

export const deleteNotificationTemplate = async (id: string) => {
  await api.delete(`/notification-templates/${id}`);
};

export const previewNotificationTemplate = async (id: string, sampleData?: Record<string, string>) => {
  const response = await api.post<ApiResponse<{ rendered: string }>>(`/notification-templates/${id}/preview`, {
    sample_data: sampleData,
  });
  return response.data.data;
};

export const sendTestNotification = async (id: string, recipient: string) => {
  const response = await api.post<ApiResponse<{ message: string }>>(`/notification-templates/${id}/send-test`, {
    recipient,
  });
  return response.data.data;
};

// =============================================================================
// Telephony Test Connections
// =============================================================================

export interface TestConnectionResult {
  success: boolean;
  message: string;
  details?: Record<string, unknown>;
}

export const testAmiConnection = async (params: {
  host: string;
  port: number;
  username: string;
  secret: string;
}): Promise<TestConnectionResult> => {
  const response = await api.post<ApiResponse<TestConnectionResult>>(
    '/settings/telephony/test-ami',
    params
  );
  return response.data.data;
};

export const testCdrConnection = async (params: {
  host: string;
  port: number;
  database: string;
  username: string;
  password: string;
  table: string;
  calldate_column?: string;
}): Promise<TestConnectionResult> => {
  const response = await api.post<ApiResponse<TestConnectionResult>>(
    '/settings/telephony/test-cdr',
    params
  );
  return response.data.data;
};

export const testWebrtcConnection = async (params: {
  sip_domain?: string;
  sip_ws_url?: string;
  stun_servers?: string[];
  turn_servers?: string[];
}): Promise<TestConnectionResult> => {
  const response = await api.post<ApiResponse<TestConnectionResult>>(
    '/settings/telephony/test-webrtc',
    params
  );
  return response.data.data;
};
